import { PieChart, Pie, Tooltip, ResponsiveContainer } from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { StatusBreakdown, QuoteStatus } from '@/api/types';

const STATUS_COLORS: Record<QuoteStatus, string> = {
  draft: '#6b7280',
  sent: '#3b82f6',
  accepted: '#22c55e',
  rejected: '#ef4444',
  invoiced: '#a855f7',
  paid: '#10b981',
};

const STATUS_KEYS: QuoteStatus[] = ['draft', 'sent', 'accepted', 'rejected', 'invoiced', 'paid'];

export function formatTooltipValue(value: number, total: number): string {
  if (total === 0) return '0%';
  return `${value} (${Math.round((value / total) * 100)}%)`;
}

interface StatusChartProps {
  data?: StatusBreakdown;
  isLoading: boolean;
}

export function StatusChart({ data, isLoading }: Readonly<StatusChartProps>) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card data-testid="chart-skeleton">
        <CardHeader className="pb-2">
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent>
          <div className="mx-auto h-[200px] w-[200px] animate-pulse rounded-full bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const total = STATUS_KEYS.reduce((sum, status) => sum + data[status], 0);
  const chartData = STATUS_KEYS.filter((status) => data[status] > 0).map((status) => ({
    name: t(`quoteStatus.${status}`),
    value: data[status],
    fill: STATUS_COLORS[status],
    color: STATUS_COLORS[status],
    key: status,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t('analytics.statusDistribution')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label={t('analytics.statusDistribution')}>
          <div className="relative">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  innerRadius="60%"
                  outerRadius="80%"
                  paddingAngle={2}
                />
                <Tooltip
                  formatter={(value: ValueType | undefined) =>
                    formatTooltipValue((value ?? 0) as number, total)
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold">{total}</span>
              <span className="text-sm text-muted-foreground">{t('analytics.chartTotal')}</span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
            {STATUS_KEYS.map((status) => (
              <div
                key={status}
                data-testid={`legend-item-${status}`}
                className={`flex items-center gap-1.5 text-sm ${data[status] === 0 ? 'opacity-40' : ''}`}
              >
                <span
                  className="inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                />
                <span>{t(`quoteStatus.${status}`)}</span>
                <span className="text-muted-foreground">{data[status]}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
